import {
    routingConfig,
    WorkflowStatus,
    type DealSummaryWorkflowStatus,
    type ShowroomSectionKey
} from '@makemydeal/dr-sales-checkin-types';
import { sectionKeyFromPathname } from '@makemydeal/dr-sales-checkin-ui-utils';

export const dealSummaryRouteMap: Record<DealSummaryWorkflowStatus, string> = {
    [WorkflowStatus.ACCEPTED]: routingConfig.DEAL_SUMMARY_ACCEPTED,
    [WorkflowStatus.DESKED]: routingConfig.DEAL_SUMMARY_DESKED,
    [WorkflowStatus.SENT_FOR_DESKING]: routingConfig.DEAL_SUMMARY_SENT
};

export const dealProposalRouteMap: Record<DealSummaryWorkflowStatus, string> = {
    ...dealSummaryRouteMap,
    [WorkflowStatus.ACCEPTED]: routingConfig.DEAL_SUMMARY_DEAL_PROPOSAL,
    [WorkflowStatus.DESKED]: routingConfig.DEAL_SUMMARY_DEAL_PROPOSAL
};

export const workflowRouteMap: Record<WorkflowStatus, string> = {
    // IDLE has no route — navigation is intentionally suppressed while bootstrapping.
    [WorkflowStatus.IDLE]: '',
    [WorkflowStatus.BOOTSTRAP_ERROR]: routingConfig.APP_ERROR,
    [WorkflowStatus.ROLE_ASSUMPTION_REQUIRED]: routingConfig.ROLE_ASSUMPTION,
    [WorkflowStatus.INVALID]: routingConfig.DEAL_SETUP,
    [WorkflowStatus.MISSING_VEHICLE]: routingConfig.INVENTORY,
    [WorkflowStatus.REDIRECT_TO_SALES_VIEW]: '',
    ...dealSummaryRouteMap,
    [WorkflowStatus.IN_PROGRESS]: routingConfig.VEHICLE_DETAILS
};

/**
 * The same destinations with every Build Your Offer section collapsed onto the one-page address:
 * under the toggle the four sections are one scrollable page, so a status that named a section now
 * names the page and the sidebar highlight follows the scroll position instead of the URL.
 *
 * Derived from the base map rather than restated, so a status whose route later names a section is
 * carried over without a second edit here. Non-section destinations (Deal Setup, Browse Inventory,
 * the error and role-assumption screens, and the two empty entries) are untouched.
 */
export const fullScrollWorkflowRouteMap: Record<WorkflowStatus, string> = {
    // IDLE has no route — navigation is intentionally suppressed while bootstrapping.
    [WorkflowStatus.IDLE]: '',
    [WorkflowStatus.BOOTSTRAP_ERROR]: routingConfig.APP_ERROR,
    [WorkflowStatus.ROLE_ASSUMPTION_REQUIRED]: routingConfig.ROLE_ASSUMPTION,
    [WorkflowStatus.INVALID]: routingConfig.DEAL_SETUP,
    [WorkflowStatus.MISSING_VEHICLE]: routingConfig.INVENTORY,
    [WorkflowStatus.REDIRECT_TO_SALES_VIEW]: '',
    [WorkflowStatus.ACCEPTED]: routingConfig.BUILD_YOUR_OFFER,
    [WorkflowStatus.DESKED]: routingConfig.BUILD_YOUR_OFFER,
    [WorkflowStatus.SENT_FOR_DESKING]: routingConfig.BUILD_YOUR_OFFER,
    [WorkflowStatus.IN_PROGRESS]: routingConfig.BUILD_YOUR_OFFER
};
/**
 * Always reads the base map: the section a status names is still a section even when the user is
 * routed to the page for it. Pointing this at `fullScrollWorkflowRouteMap` would resolve every
 * section status to the page address, which matches no section path, and the reducer that clears a
 * stale remembered section would silently stop firing.
 */
export const sectionKeyForWorkflowStatus = (status: WorkflowStatus): ShowroomSectionKey | undefined =>
    sectionKeyFromPathname(workflowRouteMap[status]);
